import { Emulator } from "../../src";

export type MemoryMatch = {
  address: number;
  value: number;
};

export type LockedAddress = {
  address: number;
  value: number;
  dataType: DataType;
  endianness: Endianness;
};

export type SavedAddress = {
  id: string;
  name: string;
  address: number;
  value: number;
  dataType: DataType;
  endianness: Endianness;
  timestamp: number;
};

export type DataType = "uint8" | "uint16" | "uint32";
export type Endianness = "little" | "big";

export class CheatManager {
  private matches: MemoryMatch[] = [];
  private searchActive = false;
  private dataType: DataType = "uint8";
  private endianness: Endianness = "little";
  private lockedAddresses: Map<number, LockedAddress> = new Map();
  private savedAddresses: SavedAddress[] = [];

  constructor(private emulator: Emulator | null = null) {}

  public setEmulator(emulator: Emulator | null): void {
    this.emulator = emulator;
    this.reset();
  }

  public reset(): void {
    this.matches = [];
    this.searchActive = false;
  }

  public getMatches(): MemoryMatch[] {
    return [...this.matches];
  }

  public getMatchCount(): number {
    return this.matches.length;
  }

  public isSearchActive(): boolean {
    return this.searchActive;
  }

  public setDataType(dataType: DataType): void {
    this.dataType = dataType;
    this.reset();
  }

  public getDataType(): DataType {
    return this.dataType;
  }

  public setEndianness(endianness: Endianness): void {
    this.endianness = endianness;
    this.reset();
  }

  public getEndianness(): Endianness {
    return this.endianness;
  }

  public getMaxValue(): number {
    switch (this.dataType) {
      case "uint8":
        return 0xff;
      case "uint16":
        return 0xffff;
      case "uint32":
        return 0xffffffff;
    }
  }

  private getDataTypeSize(): number {
    switch (this.dataType) {
      case "uint8":
        return 1;
      case "uint16":
        return 2;
      case "uint32":
        return 4;
    }
  }

  private readMultiByteValue(address: number): number {
    if (!this.emulator) {
      return 0;
    }

    const size = this.getDataTypeSize();
    let value = 0;

    if (this.endianness === "little") {
      for (let i = 0; i < size; i++) {
        value |= this.emulator.readMemory(address + i) << (i * 8);
      }
    } else {
      for (let i = 0; i < size; i++) {
        value = (value << 8) | this.emulator.readMemory(address + i);
      }
    }

    return value >>> 0;
  }

  private writeMultiByteValue(address: number, value: number): void {
    if (!this.emulator) {
      return;
    }

    const size = this.getDataTypeSize();
    value = value >>> 0;

    if (this.endianness === "little") {
      for (let i = 0; i < size; i++) {
        this.emulator.writeMemory(address + i, (value >> (i * 8)) & 0xff);
      }
    } else {
      for (let i = size - 1; i >= 0; i--) {
        this.emulator.writeMemory(address + i, (value >> (i * 8)) & 0xff);
      }
    }
  }

  public searchValue(value: number): void {
    if (!this.emulator) {
      throw new Error("No emulator instance");
    }

    const maxValue = this.getMaxValue();
    value = (value >>> 0) & maxValue;

    if (!this.searchActive) {
      this.matches = [];
      const snapshot = this.emulator.getMemorySnapshot();
      const size = this.getDataTypeSize();
      const maxAddress = snapshot.length - size + 1;

      for (let i = 0; i < maxAddress; i++) {
        const readValue = this.readMultiByteValue(i);
        if (readValue === value) {
          this.matches.push({ address: i, value });
        }
      }
      this.searchActive = true;
    } else {
      this.matches = this.matches.filter((match) => {
        const currentValue = this.readMultiByteValue(match.address);
        if (currentValue === value) {
          match.value = currentValue;
          return true;
        }
        return false;
      });
    }
  }

  public searchChanged(): void {
    if (!this.emulator) {
      throw new Error("No emulator instance");
    }

    if (!this.searchActive) {
      throw new Error("No active search");
    }

    this.matches = this.matches.filter((match) => {
      const currentValue = this.readMultiByteValue(match.address);
      if (currentValue !== match.value) {
        match.value = currentValue;
        return true;
      }
      return false;
    });
  }

  public searchIncreased(): void {
    if (!this.emulator) {
      throw new Error("No emulator instance");
    }

    if (!this.searchActive) {
      throw new Error("No active search");
    }

    this.matches = this.matches.filter((match) => {
      const currentValue = this.readMultiByteValue(match.address);
      if (currentValue > match.value) {
        match.value = currentValue;
        return true;
      }
      return false;
    });
  }

  public searchDecreased(): void {
    if (!this.emulator) {
      throw new Error("No emulator instance");
    }

    if (!this.searchActive) {
      throw new Error("No active search");
    }

    this.matches = this.matches.filter((match) => {
      const currentValue = this.readMultiByteValue(match.address);
      if (currentValue < match.value) {
        match.value = currentValue;
        return true;
      }
      return false;
    });
  }

  public writeValue(address: number, value: number): void {
    if (!this.emulator) {
      throw new Error("No emulator instance");
    }

    this.writeMultiByteValue(address, value);
  }

  public readValue(address: number): number {
    if (!this.emulator) {
      throw new Error("No emulator instance");
    }

    return this.readMultiByteValue(address);
  }

  // 地址锁定功能 / Address Locking
  public lockAddress(address: number, value: number): void {
    this.lockedAddresses.set(address, {
      address,
      value,
      dataType: this.dataType,
      endianness: this.endianness,
    });
  }

  public unlockAddress(address: number): void {
    this.lockedAddresses.delete(address);
  }

  public isAddressLocked(address: number): boolean {
    return this.lockedAddresses.has(address);
  }

  public getLockedAddresses(): LockedAddress[] {
    return Array.from(this.lockedAddresses.values());
  }

  public clearAllLocks(): void {
    this.lockedAddresses.clear();
  }

  public applyLockedValues(): void {
    if (!this.emulator) {
      return;
    }

    for (const locked of this.lockedAddresses.values()) {
      const prevDataType = this.dataType;
      const prevEndianness = this.endianness;

      this.dataType = locked.dataType;
      this.endianness = locked.endianness;

      this.writeMultiByteValue(locked.address, locked.value);

      this.dataType = prevDataType;
      this.endianness = prevEndianness;
    }
  }

  // 地址收藏功能 / Save Address
  public saveAddress(address: number, name: string): void {
    const id = `saved-${Date.now()}-${address}`;
    const value = this.readValue(address);

    this.savedAddresses.push({
      id,
      name,
      address,
      value,
      dataType: this.dataType,
      endianness: this.endianness,
      timestamp: Date.now(),
    });
  }

  public removeSavedAddress(id: string): void {
    this.savedAddresses = this.savedAddresses.filter((addr) => addr.id !== id);
  }

  public getSavedAddresses(): SavedAddress[] {
    return [...this.savedAddresses].sort((a, b) => b.timestamp - a.timestamp);
  }

  public clearSavedAddresses(): void {
    this.savedAddresses = [];
  }

  public loadSavedAddress(saved: SavedAddress): void {
    this.dataType = saved.dataType;
    this.endianness = saved.endianness;

    // 重新读取当前值并创建一个新的搜索结果
    const currentValue = this.readValue(saved.address);
    this.matches = [{ address: saved.address, value: currentValue }];
    this.searchActive = true;
  }

  // 持久化 / Persistence
  public exportData(): any {
    return {
      savedAddresses: this.savedAddresses,
      lockedAddresses: Array.from(this.lockedAddresses.entries()),
    };
  }

  public importData(data: any): void {
    if (data.savedAddresses && Array.isArray(data.savedAddresses)) {
      this.savedAddresses = data.savedAddresses;
    }
    if (data.lockedAddresses && Array.isArray(data.lockedAddresses)) {
      this.lockedAddresses = new Map(data.lockedAddresses);
    }
  }
}
